import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { HexColorPicker } from "react-colorful";
import { AppCard } from "component/shared/app-card";
import { ColorSwatch } from "../color-swatch";
import { styles } from "./style";

interface Color {
	value: string;
	label: string;
}

// Moved from the old Version & Platform page's Button Color picker, unchanged.
const colors: Color[] = [
	{ value: "#f44336", label: "Red" },
	{ value: "#e91e63", label: "Pink" },
	{ value: "#9c27b0", label: "Purple" },
	{ value: "#673ab7", label: "Deep Purple" },
	{ value: "#3f51b5", label: "Indigo" },
	{ value: "#2196f3", label: "Blue" },
	{ value: "#03a9f4", label: "Light Blue" },
	{ value: "#00bcd4", label: "Cyan" },
	{ value: "#009688", label: "Teal" },
	{ value: "#4caf50", label: "Green" },
	{ value: "#8bc34a", label: "Light Green" },
	{ value: "#cddc39", label: "Lime" },
	{ value: "#ffeb3b", label: "Yellow" },
	{ value: "#ffc107", label: "Amber" },
	{ value: "#ff9800", label: "Orange" },
	{ value: "#ff5722", label: "Deep Orange" },
	{ value: "#795548", label: "Brown" },
	{ value: "#9e9e9e", label: "Grey" },
	{ value: "#607d8b", label: "Blue Grey" },
	{ value: "#000000", label: "Black" },
];

interface Props extends WithStyles<typeof styles> {
	id: string;
	label: string;
	value: string;
	/** Shown, and swatched, while no colour is stored. Displaying it does not save it. */
	defaultValue: string;
	error?: string;
	onChange: (value: string) => void;
}

interface State {
	anchorEl: Element | null;
}

class Component extends React.Component<Props, State> {
	public constructor(props: Props) {
		super(props);
		this.state = {
			anchorEl: null,
		};
	}

	private close = () => this.setState({ anchorEl: null });

	public render() {
		const { classes = {}, id, label, value, defaultValue, error, onChange } = this.props;
		const { anchorEl } = this.state;
		// An invalid or partial hex would make an unreadable swatch, so it keeps the default.
		const swatch = value && !error ? value : defaultValue;
		return (
			<Mui.ClickAwayListener onClickAway={this.close}>
				<div className={classes.root}>
					<Mui.FormControl fullWidth error={!!error}>
						<Mui.Typography component="label" htmlFor={id} className={classes.fieldLabel}>
							{label}
						</Mui.Typography>
						<Mui.TextField
							id={id}
							fullWidth
							variant="outlined"
							value={value}
							placeholder={defaultValue}
							error={!!error}
							helperText={error}
							onChange={(event) => {
								let value = event.target.value;
								if (value && !value.startsWith("#")) {
									value = "#" + value;
								}
								onChange(value);
							}}
							InputProps={{
								startAdornment: (
									<ColorSwatch
										color={swatch}
										label={`Choose a ${label.toLowerCase()}`}
										onClick={(event) =>
											this.setState({ anchorEl: anchorEl ? null : event.currentTarget })
										}
									/>
								),
								classes: {
									root: `${classes.textFieldRoot} ${classes.inputRoot}`,
									focused: classes.textFieldFocused,
									notchedOutline: classes.textFieldNotchedOutline,
									input: classes.textFieldInput,
								},
								inputProps: {
									maxLength: 7,
									spellCheck: false,
								},
							}}
							FormHelperTextProps={{
								classes: {
									root: classes.helperText,
								},
							}}
						/>
					</Mui.FormControl>
					<Mui.Popper
						anchorEl={anchorEl}
						open={!!anchorEl}
						disablePortal
						placement="bottom-start"
						className={classes.popper}
					>
						<AppCard className={classes.card}>
							<Mui.CardContent>
								<Mui.Grid container spacing={2}>
									<Mui.Grid item>
										<HexColorPicker color={swatch} onChange={(value) => onChange(value)} />
									</Mui.Grid>
									<Mui.Grid item xs>
										<Mui.Grid container spacing={1}>
											{colors.map((color) => (
												<Mui.Grid key={color.value} item>
													<Mui.Button
														className={classes.presetButton}
														style={{ backgroundColor: color.value }}
														variant="contained"
														onClick={() => {
															onChange(color.value);
															this.close();
														}}
														title={color.label}
														aria-label={color.label}
													/>
												</Mui.Grid>
											))}
										</Mui.Grid>
									</Mui.Grid>
								</Mui.Grid>
							</Mui.CardContent>
						</AppCard>
					</Mui.Popper>
				</div>
			</Mui.ClickAwayListener>
		);
	}
}

export const PrimaryColorPicker = withStyles(Component, styles);
